import { z } from 'zod';
import { ACTOR_TYPES, STAFF_ROLES } from '../enums.js';
import { uuidSchema } from './common.js';
export const actorTypeSchema = z.enum(ACTOR_TYPES);
export const staffRoleSchema = z.enum(STAFF_ROLES);
/**
 * The resolved identity behind a request. `lib/actor-context.ts` turns this into
 * the `app.actor_type` / `app.user_id` transaction settings that every RLS
 * policy reads.
 *
 * `ownerId`, `tenantId`, `applicantId`, and `vendorId` are the scope keys.
 * Exactly one is set for a portal actor and none are set for staff — the API
 * asserts that shape rather than trusting it, because a STAFF actor carrying
 * a tenantId would be a privilege confusion bug.
 */
export const actorSchema = z
    .object({
    userId: uuidSchema,
    actorType: actorTypeSchema,
    email: z.string().email(),
    staffRole: staffRoleSchema.nullish(),
    ownerId: uuidSchema.nullish(),
    tenantId: uuidSchema.nullish(),
    applicantId: uuidSchema.nullish(),
    vendorId: uuidSchema.nullish(),
})
    .superRefine((actor, ctx) => {
    const problem = (message) => ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: ['actorType'] });
    switch (actor.actorType) {
        case 'STAFF':
            if (!actor.staffRole)
                problem('A STAFF actor must have a staff role.');
            if (actor.ownerId || actor.tenantId || actor.applicantId || actor.vendorId)
                problem('A STAFF actor must not carry an ownerId, tenantId, applicantId, or vendorId.');
            break;
        case 'OWNER':
            if (!actor.ownerId)
                problem('An OWNER actor must have an ownerId.');
            if (actor.tenantId || actor.applicantId || actor.vendorId)
                problem('An OWNER actor must not carry a tenantId, applicantId, or vendorId.');
            if (actor.staffRole)
                problem('An OWNER actor must not carry a staff role.');
            break;
        case 'TENANT':
            if (!actor.tenantId)
                problem('A TENANT actor must have a tenantId.');
            if (actor.ownerId || actor.applicantId || actor.vendorId)
                problem('A TENANT actor must not carry an ownerId, applicantId, or vendorId.');
            if (actor.staffRole)
                problem('A TENANT actor must not carry a staff role.');
            break;
        case 'APPLICANT':
            if (!actor.applicantId)
                problem('An APPLICANT actor must have an applicantId.');
            if (actor.ownerId || actor.tenantId || actor.vendorId)
                problem('An APPLICANT actor must not carry an ownerId, tenantId, or vendorId.');
            if (actor.staffRole)
                problem('An APPLICANT actor must not carry a staff role.');
            break;
        case 'VENDOR':
            if (!actor.vendorId)
                problem('A VENDOR actor must have a vendorId.');
            if (actor.ownerId || actor.tenantId || actor.applicantId)
                problem('A VENDOR actor must not carry an ownerId, tenantId, or applicantId.');
            if (actor.staffRole)
                problem('A VENDOR actor must not carry a staff role.');
            break;
        case 'PUBLIC':
            // Never backed by a users row (see PUBLIC_ACTOR in
            // lib/actor-context.ts) — so it carries no staff role and no scope
            // key, the same shape a STAFF actor's "no scope key" half has.
            if (actor.staffRole ||
                actor.ownerId ||
                actor.tenantId ||
                actor.applicantId ||
                actor.vendorId)
                problem('A PUBLIC actor must not carry a staff role or any scope id.');
            break;
    }
});
export const loginSchema = z.object({
    email: z.string().trim().toLowerCase().email(),
    password: z.string().min(1).max(512),
});
//# sourceMappingURL=actor.js.map